import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { GalleryCarouselWrapper, Button, SdkVideo } from '../../components';
import { IconAnonymousXL, ImagePlaceholderIcon, VideoPlaceholderIcon } from '../../icons';
import { FormattedMessage } from '../../util/reactIntl';
import { getCardTitles, shouldRenderImageItem } from './helpers';
import { ClockIconTiny } from '../../icons';
import { SQUARE_SMALL_2X } from '../../ducks/Assets.duck';
import { useAssets } from '../../hooks/useAssets';
import config from '../../config';
import LikeIcon from '../../forms/ProfileSettingsForm/icons/LikeIcon';
import LocationIcon from './LocationIcon';
import DotIcon from './DotIcon';
import css from './IdentitySection.css';
import { GALLERY_FILES } from '../../marketplace-custom-config';
import BadgeNewListing from './BadgeNewListing';
import BadgeReactivatedListing from './BadgeReactivatedListing';

const MODAL_BREAKPOINT = 1024;
const isWindowDefined = typeof window !== 'undefined';
const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
const {
    custom: { genders },
    anonymRiderName,
} = config;

export default ({
    currentListing,
    renderSizes,
    assetsPostponed,
    assetsLoadingRequests,
    identitySectionClassName,
    recommendationsNum,
    titleTagProp,
    isAnonymousListing: isAnonymousListingProp,
}) => {
    const paragraphEl = useRef(null);
    const [slideIndex, setSlideIndex] = useState();

    const { distanceFromSelectedPlace } = currentListing;
    const { attributes, author } = currentListing;
    const {
        publicAddress,
        gender,
        userRepresentationId,
        hasVideo,
        openForPartTimeProposals,
        anonymousListingTitle,
        hasDefaultAvatar,
    } = attributes.publicData;
    const { title } = currentListing.attributes || {};

    const listingPublicAddress = publicAddress ? publicAddress.trim() : publicAddress;
    const isUserListing = !!userRepresentationId;
    const isTemporaryListing = !isUserListing && openForPartTimeProposals === 'temporary';

    const cardTitles = getCardTitles(title, genders, gender);
    const riderTitle = isUserListing
        ? anonymousListingTitle
            ? anonymRiderName
            : (title || '').split(' ')[0]
        : title;

    const syntheticRiderListing = isUserListing
        ? {
              id: {
                  uuid: userRepresentationId,
              },
              attributes: {
                  publicData: {
                      userRepresentationId,
                  },
              },
              author: author || {},
          }
        : null;

    const loading = assetsPostponed || assetsLoadingRequests;

    const aspectWrapperClasses = classNames({
        [css.aspectWrapper]: true,
        [css.aspectWrapperNoBackground]: assetsLoadingRequests,
    });

    const locationHolderMaybe =
        listingPublicAddress ||
        distanceFromSelectedPlace ||
        isTemporaryListing ||
        recommendationsNum;

    const listingToRequestAssets = isUserListing ? syntheticRiderListing : currentListing;
    const TitleTag = titleTagProp || 'h3';

    /* useEffect */
    const images = useAssets({
        listing: assetsPostponed ? { id: { uuid: null } } : listingToRequestAssets,
        allowedTypes: ['image', 'video'],
        allowedCategories: [GALLERY_FILES],
        variant: SQUARE_SMALL_2X,
    });
    /**
     * If a rider doesn't have gallery files,
     * and there is only an avatar,
     * and this avatar is a default pic
     */
    const hasOnlyProfileImage =
        hasDefaultAvatar &&
        isUserListing &&
        images &&
        images[0] &&
        images.length === 1 &&
        images[0]?.id?._sdkType === 'UUID';

    const isLastSlideItem = slideIndex === images.length;

    return (
        <section className={css.threeToTwoWrapper}>
            <BadgeNewListing currentListing={currentListing} />
            <BadgeReactivatedListing currentListing={currentListing} />
            {!isLastSlideItem && (
                <section
                    className={classNames(css.identitySection, {
                        [identitySectionClassName]: !!identitySectionClassName,
                    })}
                >
                    <TitleTag className={css.title} ref={paragraphEl}>
                        {isUserListing ? riderTitle : cardTitles}
                    </TitleTag>
                    {locationHolderMaybe && (
                        <div className={css.locationHolder}>
                            {listingPublicAddress && (
                                <>
                                    <LocationIcon />
                                    <span>{listingPublicAddress}</span>
                                </>
                            )}
                            {!!distanceFromSelectedPlace && (
                                <div className={css.locationInfo}>
                                    <DotIcon />
                                    <span className={css.locationDisctance}>
                                        {distanceFromSelectedPlace} km
                                    </span>
                                </div>
                            )}
                            {isTemporaryListing && (
                                <div className={css.tempListing}>
                                    <ClockIconTiny />
                                    <FormattedMessage id="ListingCard.tempListing" />
                                </div>
                            )}
                            {recommendationsNum && isUserListing && (
                                <p className={css.recommendationsNumSection}>
                                    <LikeIcon /> <span>{recommendationsNum} </span>
                                    <FormattedMessage id="ProfileSettingsPage.recommendations" />
                                </p>
                            )}
                        </div>
                    )}
                </section>
            )}
            <div className={aspectWrapperClasses}>
                {loading ? (
                    <div className={css.loadingOverlay}>
                        {hasVideo ? <VideoPlaceholderIcon /> : <ImagePlaceholderIcon />}
                    </div>
                ) : images && images.length ? (
                    <GalleryCarouselWrapper
                        dragEnabled
                        pagination
                        touchEnabled={isMobileLayout}
                        items={images}
                        renderSizes={renderSizes}
                        notifyOnSlideChange={slideIndex => setSlideIndex(slideIndex)}
                        rootClassName={css.galleryCarouselWrapper}
                        actionItem={{
                            type: 'actionItem',
                            content: (
                                <div className={css.actionItem}>
                                    <Button>
                                        <FormattedMessage
                                            id={`ListingCard.actionItem-${
                                                isUserListing ? 'rider' : 'horse'
                                            }`}
                                        />
                                    </Button>
                                </div>
                            ),
                        }}
                        renderCarouselItem={item => {
                            const itemVariant = shouldRenderImageItem(item);
                            const isVideo = item && item.type === 'video';
                            if (!itemVariant && !isVideo) return null;
                            const isAnonymousListing =
                                !item?.isDefaultAvatar &&
                                isAnonymousListingProp &&
                                !hasOnlyProfileImage;

                            return (
                                <>
                                    <div
                                        className={classNames(css.rootForImage, {
                                            [css.isAnonymousListing]: isAnonymousListing,
                                        })}
                                    >
                                        <div
                                            className={css.wallpaper}
                                            style={
                                                itemVariant
                                                    ? {
                                                          backgroundImage: `url('${itemVariant}')`,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {isVideo ? (
                                                <SdkVideo rootClassName={css.video} entity={item} />
                                            ) : null}
                                        </div>
                                    </div>
                                    <code
                                        className={classNames(css.overlayBottom, {
                                            [css.isAnonymousListing]: isAnonymousListing,
                                        })}
                                    >
                                        <IconAnonymousXL />
                                    </code>
                                </>
                            );
                        }}
                    />
                ) : null}
            </div>
        </section>
    );
};
